import { useLiveQuery } from 'dexie-react-hooks';
import { useGetVersionsQuery } from '../youversion';
import { bibleDB } from '../database';
import { APIVersion, Version } from '../types';
import { Virtuoso } from 'react-virtuoso';
import { Button } from '../../ui/components/Button';
import { ArrowDownTrayIcon, TrashIcon } from '@heroicons/react/24/solid';
import { beginDownload, removeVersion } from '../functions';
import { usePluginContext } from '../../plugin/hooks';
import { LoadingProgress } from './LoadingProgress';
import { useMemo, useState } from 'react';
import { ConfirmButton } from '../../ui/components/ConfirmButton';

type VersionProps = {
  downloadedVersion?: Version,
  version: APIVersion
}

const VersionView = ({ downloadedVersion, version }: VersionProps) => {
  const plugin = usePluginContext();
  return (
    <div className='bg-white text-neutral-800 hover:bg-blue-100 p-4 rounded-sm m-1 flex justify-between items-center'>
      <span className='grow'>
        {version.local_title}
        {' '}
        <span className='text-neutral-400'>[{version.local_abbreviation}]</span>
      </span>
      <div className='basis-10'>
        {
          downloadedVersion?.download_state === 'complete' && (
            <div className='flex items-center'>
              <span>Downloaded</span>
              <ConfirmButton
                className='ml-2 bg-red-400 hover:bg-red-300'
                onConfirm={() => removeVersion(version.id)}
              >
                <TrashIcon className='size-4' />
              </ConfirmButton>
            </div>
          )
        }
        {
          downloadedVersion?.download_state === 'pending' && (
            <LoadingProgress caption='Downloading' />
          )
        }
        {
          downloadedVersion?.download_state === 'extracting' && (
            <LoadingProgress caption='Installing' />
          )
        }
        {
          !downloadedVersion && version.offline.url && (
            <Button
              grow={false}
              onClick={() => beginDownload(plugin, version)}
            >
              <ArrowDownTrayIcon className='size-6 mr-1' />
              Download
            </Button>
          )
        }
      </div>
    </div>
  );
};

export const BibleManager = () => {

  const availableVersions = useGetVersionsQuery('eng');
  const downloadedVersions = useLiveQuery(() => bibleDB.versions.toArray());

  const [filterText, setFilterText] = useState<string>('');

  const filteredAvailable = useMemo(
    () => {
      if (availableVersions.isSuccess) {
        return availableVersions.data.filter(
          v => v.local_title.toLowerCase().includes(filterText) ||
                 v.abbreviation.toLowerCase().includes(filterText) ||
                 v.local_abbreviation.toLowerCase().includes(filterText)
        );
      }
      return [];
    },
    [availableVersions, filterText]
  );

  if (availableVersions.isFetching) {
    return (
      <div className='my-auto'>
        <LoadingProgress caption='Loading version list...' />
      </div>
    );
  }

  const renderResult = (_: number, version: APIVersion) => (
    <VersionView
      downloadedVersion={downloadedVersions && downloadedVersions.find(b => b.id === version.id)}
      version={version}
    />
  );

  return (
    <>
      <input
        className='rounded-sm m-2 p-2 text-neutral-800 self-stretch'
        onChange={(e) => setFilterText(e.target.value.toLowerCase())}
        placeholder='Filter available Bibles'
        value={filterText}
      />
      <div className='grow overflow-y-auto min-h-0 w-full'>
        <Virtuoso
          data={filteredAvailable}
          itemContent={renderResult}
          totalCount={filteredAvailable.length}
        />
      </div>
    </>
  );
};
